// engine/Pathing.ts — Pathing interface + a naive GreedyPathing and an optimal
// AStarPathing, both deterministic and allocation-free per call.
//
// GreedyPathing picks the admissible 8-neighbour step that most reduces the
// Chebyshev distance to the target. It is allocation-free (returns a Direction
// index, never an object) and deterministic: ties are broken by the stable DIR
// order (N, NE, E, SE, S, SW, W, NW).
//
// AStarPathing implements the SAME `Pathing` interface, so it drops in behind any
// caller without a wiring change (GreedyPathing stays the default everywhere). It
// runs a real A* over the 8-neighbour grid with the admissible Chebyshev
// heuristic and returns the FIRST step (in stable DIR order) along an optimal
// path. All of its working memory is preallocated in the constructor and reused
// each call via a generation-stamp visited marker, so a step allocates nothing.
//
// WEIGHTED + BOUNDED A* (the avoidance layer). AStarPathing optionally consults a
// PathCostField: an attachable, per-tile EXTRA cost (>= 0) added on top of the
// base step. With a field, the step cost into (nx,ny) is `1 + extraCostAt(nx,ny)`,
// so a forager can be routed AROUND a high-cost zone (e.g. a spider's territory)
// rather than straight through it. The Chebyshev heuristic stays admissible
// because the minimum possible step cost is still 1 (extra is never negative), so
// A* remains a true shortest-(weighted-)path search. A configurable expansion cap
// (default grid.size, the original behaviour) keeps each search bounded for the
// per-tick hot path; on a budget-out the search returns the first step of the
// best partial progress made toward the goal, so a forager still advances.
//
// NO field + the default cap ⇒ AStarPathing is BYTE-IDENTICAL to the original
// (the cost reduces to the uniform `g+1`, and the cap reduces to grid.size).

import { DIR, DIR_DX, DIR_DY, DIR_COUNT } from '../types.ts';
import type { Direction, Pathing } from '../types.ts';
import type { WorldGrid } from './WorldGrid.ts';

/** Chebyshev (king-move) distance — the metric for 8-directional movement. */
export function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax > bx ? ax - bx : bx - ax;
  const dy = ay > by ? ay - by : by - ay;
  return dx > dy ? dx : dy;
}

/**
 * An OPTIONAL, attachable per-tile extra-cost layer for weighted pathing. A cost
 * field returns a NON-NEGATIVE integer extra cost for entering tile (x,y); the
 * pathing adds it to the uniform base step cost (1). A zero everywhere field is
 * indistinguishable from no field at all (the search reduces to uniform-cost).
 *
 * Implementations MUST be cheap (it's called per neighbour per expansion in the
 * hot path), integer, and deterministic — the same (x,y) returns the same value
 * within a single tick. The DangerField (sim/dangerField.ts) is the canonical
 * implementation: predator territory reads as a high cost that falls to 0 outside
 * a threat radius.
 */
export interface PathCostField {
  /** Non-negative extra cost (>= 0) of entering tile (x,y). 0 ⇒ no penalty. */
  extraCostAt(x: number, y: number): number;
}

export class GreedyPathing implements Pathing {
  private readonly grid: WorldGrid;

  constructor(grid: WorldGrid) {
    this.grid = grid;
  }

  /**
   * One greedy step from (fromX,fromY) toward (toX,toY). Returns the best
   * admissible Direction, or DIR.Stay if already there or fully blocked.
   *
   * "Admissible" = in bounds, passable, and below the ant capacity cap
   * (via grid.canMoveAnt). Among admissible neighbours we take the one with the
   * smallest resulting Chebyshev distance; the stable DIR iteration order makes
   * ties deterministic.
   */
  step(fromX: number, fromY: number, toX: number, toY: number): Direction {
    if (fromX === toX && fromY === toY) return DIR.Stay;

    const current = chebyshev(fromX, fromY, toX, toY);
    let bestDir: Direction = DIR.Stay;
    let bestDist = current; // only accept strictly-improving (or equal) moves below

    for (let d = 0; d < DIR_COUNT; d++) {
      const nx = fromX + (DIR_DX[d] as number);
      const ny = fromY + (DIR_DY[d] as number);
      if (!this.grid.canMoveAnt(nx, ny)) continue;
      const dist = chebyshev(nx, ny, toX, toY);
      // Strictly-less keeps the FIRST direction (stable order) when distances
      // tie, and guarantees progress (no oscillation between equal tiles).
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = d as Direction;
      }
    }
    return bestDir;
  }
}

/**
 * Optimal A* pathing behind the same `Pathing` contract as GreedyPathing.
 *
 * Cost model: every one of the 8 king-moves costs exactly 1 (diagonals are NOT
 * more expensive — the same uniform movement model the rest of the engine uses,
 * one tile per step). The heuristic is the Chebyshev distance, which is exactly
 * the cost of an obstacle-free path under that model, so it is admissible AND
 * consistent — A* returns a true shortest path.
 *
 * Passability uses grid.canMoveAnt (the same rule GreedyPathing honours), so the
 * two agree on an open field. Where greedy can wedge itself in a local minimum
 * (a wall between it and the target), A* routes around it.
 *
 * Allocation-free per call: the g-score, root-direction, binary-heap, and
 * heap-position buffers are sized to grid.size in the constructor and reused. A
 * per-cell generation stamp (`seen`/`closed`) is bumped each step instead of
 * clearing the buffers, so a call costs O(expanded · log open), never O(size).
 *
 * Determinism + tie-breaking: the open set is a binary min-heap ordered by a
 * STABLE total order — lower f, then (tie) lower g, then (tie) lower FIRST-STEP
 * direction (DIR order), then (tie) lower linear cell index. Each cell carries the
 * DIR index of the first step on its current-best path (`rootDir`), propagated
 * from the start, so among the many EQUALLY-OPTIMAL paths A* commits to the one
 * whose first move has the lowest DIR index. That is exactly GreedyPathing's
 * tie-break, so on an obstacle-free field the two return the identical Direction;
 * the result is reproducible for identical inputs every run.
 *
 * Weighting (optional cost field): the step cost into a neighbour is `1 +
 * field.extraCostAt(nx,ny)` (the field returns >= 0). Heuristic stays Chebyshev,
 * still admissible (min step cost is 1), so A* returns a true shortest WEIGHTED
 * path — i.e. it detours around expensive tiles when that lowers total cost. With
 * no field the cost reduces to the original uniform `g+1`.
 *
 * Bounding (configurable cap): the search expands at most `maxExpansions` cells
 * (default grid.size, the original bound). On a budget-out BEFORE reaching the
 * goal it returns the first step (`rootDir`) of the lowest-f cell it managed to
 * expand — the best progress toward the goal — so a forager keeps advancing
 * instead of freezing. A genuinely unreachable / boxed-in / at-target query still
 * returns DIR.Stay (no expanded cell ever improved on the start).
 */
export class AStarPathing implements Pathing {
  private readonly grid: WorldGrid;
  /** Hard cap on expansions per step() — bounds the per-tick cost. */
  private readonly maxExpansions: number;
  /** Optional attached per-tile extra-cost layer (weighted pathing). */
  private readonly costField: PathCostField | undefined;

  /** Cost-so-far from the start to each cell (valid only when seen === gen). */
  private readonly gScore: Int32Array;
  /**
   * DIR index of the FIRST step out of the start on this cell's current-best
   * path. Propagated from the start during relaxation; the goal's rootDir is the
   * answer step. Also the cell-level tie-breaker that aligns A* with greedy.
   */
  private readonly rootDir: Int32Array;
  /** Generation stamp marking a cell as discovered this call (vs a stale buffer). */
  private readonly seen: Int32Array;
  /** Generation stamp marking a cell as finalized (popped) this call. */
  private readonly closed: Int32Array;

  // --- Binary min-heap of open cells (linear indices), keyed by f/g/index. ---
  /** Heap storage of cell indices; valid range is [0, heapCount). */
  private readonly heap: Int32Array;
  /** f-score (g + h) parallel to the heap entry, cached to avoid recompute. */
  private readonly heapF: Int32Array;
  /** Each open cell's slot in `heap` (for decrease-key); -1 if not in the heap. */
  private readonly heapPos: Int32Array;
  private heapCount = 0;

  /** Bumped each step() so stale stamps from the previous call read as unseen. */
  private gen = 0;

  /**
   * @param grid           the world grid (sizes all buffers).
   * @param maxExpansions  hard cap on cells expanded per step(); default grid.size
   *                       (the original, unbounded-for-practical-purposes bound).
   *                       A smaller cap keeps per-tick A* cheap for the hot path.
   * @param costField      optional attachable per-tile extra-cost layer (weighted
   *                       pathing). Absent ⇒ uniform `g+1` (byte-identical to the
   *                       original A*).
   */
  constructor(grid: WorldGrid, maxExpansions?: number, costField?: PathCostField) {
    this.grid = grid;
    const n = grid.size;
    // Default cap = grid.size (every cell is finalized at most once), preserving
    // the original behaviour exactly when the caller passes nothing.
    this.maxExpansions = maxExpansions === undefined ? n : maxExpansions;
    this.costField = costField;
    this.gScore = new Int32Array(n);
    this.rootDir = new Int32Array(n);
    this.seen = new Int32Array(n);
    this.closed = new Int32Array(n);
    this.heap = new Int32Array(n);
    this.heapF = new Int32Array(n);
    this.heapPos = new Int32Array(n).fill(-1);
  }

  /**
   * One step from (fromX,fromY) toward (toX,toY) along an optimal path. Returns
   * the first Direction on that path, or DIR.Stay if already there, the target is
   * impassable/unreachable, or the start itself can't be stood on (defensive).
   */
  step(fromX: number, fromY: number, toX: number, toY: number): Direction {
    if (fromX === toX && fromY === toY) return DIR.Stay;
    const grid = this.grid;
    // The target must be a tile we could actually finish on; if not, no path.
    if (!grid.canMoveAnt(toX, toY)) return DIR.Stay;

    // New generation: every cell's stamp now reads as unseen without clearing.
    const gen = ++this.gen;
    this.heapCount = 0;

    const start = grid.index(fromX, fromY);
    const goal = grid.index(toX, toY);

    this.gScore[start] = 0;
    this.rootDir[start] = DIR.Stay; // start has no first step yet
    this.seen[start] = gen;
    this.heapPush(start, chebyshev(fromX, fromY, toX, toY));

    const field = this.costField;

    // Bound expansions by the configured cap (default grid.size). Every cell is
    // finalized at most once, so this can never loop on an unreachable target.
    let expansions = 0;
    const maxExpansions = this.maxExpansions;

    // Best-partial-progress fallback for a BUDGET-OUT: track the expanded cell
    // (other than the start) with the lowest f seen — i.e. the most promising
    // step toward the goal. If the cap is hit before the goal is reached, we
    // return THIS cell's first step so the agent keeps advancing instead of
    // freezing. `bestRoot` is DIR.Stay until an off-start cell is expanded.
    let bestF = 0x7fffffff;
    let bestRoot: Direction = DIR.Stay;

    while (this.heapCount > 0 && expansions < maxExpansions) {
      const cur = this.heapPop();
      // The goal's rootDir is the first step on its optimal, lowest-DIR path.
      if (cur === goal) return this.rootDir[cur] as Direction;
      this.closed[cur] = gen;
      expansions++;

      // Record best partial progress (lowest f among expanded off-start cells).
      // f at pop time = gScore + Chebyshev-to-goal; the start (rootDir Stay) is
      // never a real step so it's excluded. Strict-less keeps the FIRST (lowest
      // DIR / lowest index, by the heap order) on ties — deterministic.
      if (cur !== start) {
        const fcur = (this.gScore[cur] as number) + chebyshev(
          cur % grid.width, (cur - (cur % grid.width)) / grid.width, toX, toY,
        );
        if (fcur < bestF) {
          bestF = fcur;
          bestRoot = this.rootDir[cur] as Direction;
        }
      }

      const cx = cur % grid.width;
      const cy = (cur - cx) / grid.width;
      const g0 = this.gScore[cur] as number;

      for (let d = 0; d < DIR_COUNT; d++) {
        const nx = cx + (DIR_DX[d] as number);
        const ny = cy + (DIR_DY[d] as number);
        if (!grid.canMoveAnt(nx, ny)) continue;
        const ni = grid.index(nx, ny);
        if (this.closed[ni] === gen) continue;

        // Step cost: uniform 1 plus the optional field's extra cost for entering
        // (nx,ny). No field ⇒ just g0 + 1 (the original uniform cost).
        const tentative = g0 + 1 + (field !== undefined ? field.extraCostAt(nx, ny) : 0);

        // The first step on a path THROUGH cur is cur's own first step, except
        // when cur IS the start — then this neighbour IS the first step (d).
        const root = cur === start ? d : (this.rootDir[cur] as number);

        if (this.seen[ni] !== gen) {
          // First time we reach this cell.
          this.seen[ni] = gen;
          this.gScore[ni] = tentative;
          this.rootDir[ni] = root;
          this.heapPush(ni, tentative + chebyshev(nx, ny, toX, toY));
        } else if (tentative < (this.gScore[ni] as number)) {
          // A strictly cheaper route to a still-open cell.
          this.gScore[ni] = tentative;
          this.rootDir[ni] = root;
          this.heapDecrease(ni, tentative + chebyshev(nx, ny, toX, toY));
        } else if (
          tentative === (this.gScore[ni] as number) &&
          root < (this.rootDir[ni] as number) &&
          (this.heapPos[ni] as number) >= 0
        ) {
          // Same cost, but via a lower-DIR first step — prefer it (the greedy
          // tie-break). f is unchanged, but the heap order depends on rootDir, so
          // re-sift in place; only matters while the cell is still open.
          this.rootDir[ni] = root;
          this.heapReorder(ni);
        }
      }
    }
    // The loop ended. Two distinct reasons:
    //  • the heap emptied (heapCount === 0): the goal is genuinely UNREACHABLE
    //    (the whole reachable region was searched) ⇒ DIR.Stay, exactly the
    //    original behaviour (so no-field + default-cap stays byte-identical).
    //  • the cap was hit with cells still queued (heapCount > 0): a BUDGET-OUT ⇒
    //    return the best partial progress so the agent still advances. With the
    //    default cap (grid.size) this branch is unreachable (every cell finalizes
    //    at most once), so it can never alter the original A*'s answers.
    if (this.heapCount > 0) return bestRoot;
    return DIR.Stay;
  }

  // --- Binary min-heap (deterministic comparator) -----------------------------
  //
  // Strict total order: f asc, then g asc, then first-step DIR asc, then linear
  // cell index asc — so pops are fully deterministic and align with greedy.

  /** True if heap entry `a` should sort before heap entry `b` (strict). */
  private less(a: number, b: number): boolean {
    const fa = this.heapF[a] as number;
    const fb = this.heapF[b] as number;
    if (fa !== fb) return fa < fb;
    const ca = this.heap[a] as number;
    const cb = this.heap[b] as number;
    const ga = this.gScore[ca] as number;
    const gb = this.gScore[cb] as number;
    if (ga !== gb) return ga < gb;
    const ra = this.rootDir[ca] as number;
    const rb = this.rootDir[cb] as number;
    if (ra !== rb) return ra < rb;
    return ca < cb; // final tie-break: lower cell index
  }

  /** Swap two heap slots and keep their position map in sync. */
  private swap(a: number, b: number): void {
    const ca = this.heap[a] as number;
    const cb = this.heap[b] as number;
    this.heap[a] = cb;
    this.heap[b] = ca;
    const fa = this.heapF[a] as number;
    this.heapF[a] = this.heapF[b] as number;
    this.heapF[b] = fa;
    this.heapPos[ca] = b;
    this.heapPos[cb] = a;
  }

  private heapPush(cell: number, f: number): void {
    const i = this.heapCount++;
    this.heap[i] = cell;
    this.heapF[i] = f;
    this.heapPos[cell] = i;
    this.siftUp(i);
  }

  private heapPop(): number {
    const top = this.heap[0] as number;
    const last = --this.heapCount;
    if (last > 0) {
      this.heap[0] = this.heap[last] as number;
      this.heapF[0] = this.heapF[last] as number;
      this.heapPos[this.heap[0] as number] = 0;
      this.siftDown(0);
    }
    this.heapPos[top] = -1;
    return top;
  }

  /** Lower an open cell's f and restore the heap (decrease-key). */
  private heapDecrease(cell: number, f: number): void {
    const i = this.heapPos[cell] as number;
    if (i < 0) return; // not open (shouldn't happen for a seen-but-open cell)
    this.heapF[i] = f;
    this.siftUp(i);
  }

  /**
   * Re-establish heap order for an open cell whose tie-break key (rootDir) was
   * LOWERED but whose f did not change. A lower rootDir sorts strictly earlier, so
   * the cell can only need to move toward the root — siftUp alone is correct.
   */
  private heapReorder(cell: number): void {
    const i = this.heapPos[cell] as number;
    if (i < 0) return;
    this.siftUp(i);
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.less(i, parent)) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  private siftDown(i: number): void {
    const n = this.heapCount;
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let smallest = i;
      if (l < n && this.less(l, smallest)) smallest = l;
      if (r < n && this.less(r, smallest)) smallest = r;
      if (smallest === i) break;
      this.swap(i, smallest);
      i = smallest;
    }
  }
}
